import { useCallback, useRef } from 'react';

import { DEFAULT_MAP_ZOOM, STATIC_MAP_ZOOM, defaultInitialZoom } from 'features/store/constants';

import { CenterPointExtended } from 'hooks/use-map-extended/types';
import useAppleMap from 'hooks/use-map-extended/use-apple-map.web';
import { useLocale } from 'state/intl';
import { isValidPosition } from 'utils/geolocation';
import noop from 'utils/noop';

import mapDefaults from './defaults';
import { deriveMarkerIcon } from './icons/index.web';
import {
  CenterPoint,
  FitAndCenterFromCoordsFn,
  IMarkerInput,
  IUseMapArgs,
  MarkerTypes,
  PanToFn,
} from './types';

/**
 * Apple MapKit adapter that adapts useAppleMap to useMap interface
 */
const useMapAppleAdapter = (
  { eventListeners = {}, position, disableControls = false, isStaticMap = false }: IUseMapArgs = {},
  enabled = true
) => {
  const { region } = useLocale();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const startPos = position || defaultPosition;

  // Zoom logic:
  // - If isStaticMap (single location view like order confirmation), use STATIC_MAP_ZOOM
  // - If position is provided, use DEFAULT_MAP_ZOOM
  // - Otherwise use defaultPosition.zoom
  const zoomLevel = isStaticMap
    ? STATIC_MAP_ZOOM
    : position
    ? DEFAULT_MAP_ZOOM
    : defaultPosition.zoom ?? defaultInitialZoom;

  const startPosition: CenterPointExtended = {
    lat: startPos.lat,
    lng: startPos.lng,
    zoom: zoomLevel as number,
  };

  const {
    mapComponent,
    createMarker: appleCreateMarker,
    createMarkerList: appleCreateMarkerList,
    focusToPoint,
    focusToPoints,
    getCurrentCenter,
  } = useAppleMap(
    {
      startPosition,
      disableControls,
      onDragEnd: () => {
        const handlers = eventListeners.dragend;
        if (typeof handlers === 'function') {
          handlers();
        }
        if (Array.isArray(handlers)) {
          handlers.forEach(handler => handler());
        }
      },
    },
    enabled
  );

  const markersRef = useRef<Record<string, IMarkerInput>>({});

  // Adapt createMarker to match useMap interface
  const createMarker = useCallback(
    (markerInput: IMarkerInput): string => {
      const { id, type, location, onPress, title, isSelected } = markerInput;

      if (!isValidPosition(location)) {
        return id;
      }

      markersRef.current[id] = markerInput;

      // Call Apple's createMarker with the adapted format
      appleCreateMarker({
        id,
        type,
        location,
        onPress: onPress || noop,
        title,
        isSelected,
        icon: deriveMarkerIcon(type),
        preventMarkerPress: type === MarkerTypes.User,
        visible: type !== MarkerTypes.UserManual,
      });

      return id;
    },
    [appleCreateMarker]
  );

  const clearMarkers = useCallback(() => {
    markersRef.current = {};
  }, []);

  const createMarkerList = useCallback(
    (markerList: IMarkerInput[]) => {
      const adaptedMarkers = markerList.map(marker => ({
        ...marker,
        icon: deriveMarkerIcon(marker.type),
        preventMarkerPress: marker.type === MarkerTypes.User,
        visible: marker.type !== MarkerTypes.UserManual,
        onPress: marker.onPress || noop,
      }));
      appleCreateMarkerList(adaptedMarkers);
    },
    [appleCreateMarkerList]
  );

  // Adapt panTo to match useMap interface
  const panTo = useCallback<PanToFn>(
    async newPosition => {
      if (isValidPosition(newPosition)) {
        focusToPoint(newPosition as CenterPoint);
      }
    },
    [focusToPoint]
  );

  // Adapt fitAndCenterFromCoords
  const fitAndCenterFromCoords = useCallback<FitAndCenterFromCoordsFn>(
    (coordsArray: CenterPoint[]) => {
      if (coordsArray.length > 0) {
        focusToPoints(coordsArray);
      }
    },
    [focusToPoints]
  );

  const currentCenter = getCurrentCenter();

  return {
    center: { lat: currentCenter.lat, lng: currentCenter.lng },
    createMarker,
    createMarkerList,
    clearMarkers,
    fitAndCenterFromCoords,
    map: mapComponent,
    panTo,
    updateMarkerPosition: noop,
    zoom: currentCenter.zoom || defaultInitialZoom,
  };
};

export default useMapAppleAdapter;
